import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreateItemModal = ({ isOpen, onClose, onSave, selectedFolder }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'password',
    url: '',
    username: '',
    password: '',
    notes: '',
    tags: [],
    folder: selectedFolder?.id || ''
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');

  const itemTypes = [
    { value: 'password', label: 'Password', icon: 'Key' },
    { value: 'api-key', label: 'API Key', icon: 'Code' },
    { value: 'ssh-key', label: 'SSH Key', icon: 'Terminal' },
    { value: 'database', label: 'Database', icon: 'Database' },
    { value: 'secure-note', label: 'Secure Note', icon: 'FileText' }
  ];

  const templates = [
    { id: 'blank', name: 'Blank Item', description: 'Start from scratch' },
    { id: 'website', name: 'Website Login', description: 'Username and password for websites' },
    { id: 'database', name: 'Database Connection', description: 'Database credentials with connection string' },
    { id: 'api', name: 'API Credentials', description: 'API keys and tokens' },
    { id: 'server', name: 'Server Access', description: 'SSH keys and server credentials' }
  ];

  const generatePassword = () => {
    const length = 16;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset?.charAt(Math.floor(Math.random() * charset?.length));
    }
    setFormData({ ...formData, password });
    checkPasswordStrength(password);
  };

  const checkPasswordStrength = (password) => {
    if (password?.length < 8) {
      setPasswordStrength('weak');
    } else if (password?.length < 12 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/?.test(password)) {
      setPasswordStrength('medium');
    } else {
      setPasswordStrength('strong');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (field === 'password') {
      checkPasswordStrength(value);
    }
  };

  const handleSave = () => {
    const newItem = {
      id: Date.now()?.toString(),
      ...formData,
      createdAt: new Date()?.toISOString(),
      lastModified: new Date()?.toISOString(),
      passwordStrength: formData?.type === 'password' ? passwordStrength : null,
      isFavorite: false,
      isShared: false,
      hasAttachments: false
    };
    
    onSave(newItem);
    onClose();
    
    // Reset form
    setFormData({
      name: '',
      type: 'password',
      url: '',
      username: '',
      password: '',
      notes: '',
      tags: [],
      folder: selectedFolder?.id || ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Add New Item</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Template Selection */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Choose Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates?.map(template => (
                <div
                  key={template?.id}
                  className="p-3 border border-border rounded-lg cursor-pointer hover:bg-muted transition-smooth"
                  onClick={() => {
                    // Apply template logic here
                    if (template?.id === 'website') {
                      setFormData({ ...formData, type: 'password' });
                    } else if (template?.id === 'database') {
                      setFormData({ ...formData, type: 'database' });
                    } else if (template?.id === 'api') {
                      setFormData({ ...formData, type: 'api-key' });
                    } else if (template?.id === 'server') {
                      setFormData({ ...formData, type: 'ssh-key' });
                    }
                  }}
                >
                  <h4 className="font-medium text-foreground">{template?.name}</h4>
                  <p className="text-sm text-muted-foreground">{template?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Item Name"
                type="text"
                placeholder="Enter item name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                required
              />
              
              <Select
                label="Item Type"
                options={itemTypes}
                value={formData?.type}
                onChange={(value) => handleInputChange('type', value)}
              />
            </div>

            {(formData?.type === 'password' || formData?.type === 'database') && (
              <Input
                label="Website/URL"
                type="url"
                placeholder="https://example.com"
                value={formData?.url}
                onChange={(e) => handleInputChange('url', e?.target?.value)}
              />
            )}
          </div>

          {/* Credentials */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Credentials</h3>
            
            {(formData?.type === 'password' || formData?.type === 'database') && (
              <Input
                label="Username/Email"
                type="text"
                placeholder="Enter username or email"
                value={formData?.username}
                onChange={(e) => handleInputChange('username', e?.target?.value)}
              />
            )}

            {(formData?.type === 'password' || formData?.type === 'database') && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-foreground">Password</label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generatePassword}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Generate
                  </Button>
                </div>
                
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter or generate password"
                    value={formData?.password}
                    onChange={(e) => handleInputChange('password', e?.target?.value)}
                    className="pr-10"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-6 h-6"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={14} />
                  </Button>
                </div>
                
                {formData?.password && passwordStrength && (
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      passwordStrength === 'strong' ? 'bg-success' :
                      passwordStrength === 'medium' ? 'bg-warning' : 'bg-error'
                    }`} />
                    <span className={`text-sm ${
                      passwordStrength === 'strong' ? 'text-success' :
                      passwordStrength === 'medium' ? 'text-warning' : 'text-error'
                    }`}>
                      {passwordStrength?.charAt(0)?.toUpperCase() + passwordStrength?.slice(1)} password
                    </span>
                  </div>
                )}
              </div>
            )}

            {formData?.type === 'api-key' && (
              <div className="space-y-4">
                <Input
                  label="API Key"
                  type="password"
                  placeholder="Enter API key"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                />
                <Input
                  label="API Endpoint"
                  type="url"
                  placeholder="https://api.example.com"
                  value={formData?.url}
                  onChange={(e) => handleInputChange('url', e?.target?.value)}
                />
              </div>
            )}

            {formData?.type === 'ssh-key' && (
              <div className="space-y-4">
                <Input
                  label="Server Address"
                  type="text"
                  placeholder="server.example.com"
                  value={formData?.url}
                  onChange={(e) => handleInputChange('url', e?.target?.value)}
                />
                <Input
                  label="Username"
                  type="text"
                  placeholder="root"
                  value={formData?.username}
                  onChange={(e) => handleInputChange('username', e?.target?.value)}
                />
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Additional Information</h3>
            
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Notes</label>
              <textarea
                placeholder="Add any additional notes or information"
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                className="w-full p-3 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                rows={3}
              />
            </div>

            <Input
              label="Tags (comma separated)"
              type="text"
              placeholder="production, critical, shared"
              value={formData?.tags?.join(', ')}
              onChange={(e) => handleInputChange('tags', e?.target?.value?.split(',')?.map(tag => tag?.trim())?.filter(Boolean))}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleSave}
            disabled={!formData?.name?.trim()}
          >
            Save Item
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateItemModal;